import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, List, Spin, Tag, Typography, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { axiosRDV } from "../api/axios";
import useAuthSession from "../authPatient/hooks/useAuthSession";
import { selectAuthUser } from "../authPatient/redux/selectors/authSelectors";
import { fetchUserById } from "../Profile/redux/thunks/userThunks";
import { fetchNotifications } from "../notifications/redux/notificationsThunks";
import {
  invalidateMedicalCapabilities,
  MEDICAL_ACCESS_SCOPE_OPTIONS,
} from "./useMedicalCapabilities";

const scopeLabels = new Map(
  MEDICAL_ACCESS_SCOPE_OPTIONS.map(({ value, label }) => [value, label]),
);

const unwrapList = (value) => {
  const payload = value?.data ?? value;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export default function PatientMedicalAccessPage() {
  const { activeSubprofileId } = useAuthSession({ auto: true });
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const authenticatedId = String(
    user?.id ?? user?._id ?? user?.userId ?? user?.sub ?? "",
  ).trim();
  const hydratedUser = useSelector((state) =>
    state.user?.usersById?.[authenticatedId] ||
    state.user?.usersById?.[authenticatedId.toLowerCase()] ||
    null,
  );
  const patientId = String(
    authenticatedId ||
      hydratedUser?.patientId ||
      hydratedUser?.id ||
      hydratedUser?._id ||
      "",
  ).trim();
  const effectiveProfileId = String(activeSubprofileId ?? patientId).trim();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    if (authenticatedId && !hydratedUser) {
      dispatch(fetchUserById(authenticatedId));
    }
  }, [authenticatedId, dispatch, hydratedUser]);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosRDV.get(
        `/access-requests/patient/${encodeURIComponent(patientId)}`,
      );
      setItems(unwrapList(data));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Impossible de charger les autorisations.",
      );
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      load();
      dispatch(fetchNotifications({
        skip: 0,
        limit: 20,
        userId: patientId,
      }));
    }
  }, [dispatch, load, patientId]);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          new Date(b?.updatedAt || b?.createdAt || 0).getTime() -
          new Date(a?.updatedAt || a?.createdAt || 0).getTime(),
      ),
    [items],
  );

  const revoke = async (grant) => {
    setRevokingId(grant.id);
    try {
      await axiosRDV.post(`/access-requests/${grant.id}/revoke`, {
        subprofileId: grant.subprofileId ?? null,
      });
      invalidateMedicalCapabilities(effectiveProfileId, grant.doctorId, [
        patientId,
      ]);
      message.success("Accès médecin révoqué.");
      await load();
    } catch (requestError) {
      message.error(
        requestError?.response?.data?.message ||
          "La révocation a échoué.",
      );
    } finally {
      setRevokingId(null);
    }
  };

  if (!patientId || loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: 240 }}>
        <Spin />
      </div>
    );
  }

  return (
    <main style={{ maxWidth: 920, margin: "32px auto", padding: "0 20px" }}>
      <Typography.Title level={2}>Accès à mon dossier médical</Typography.Title>
      <Typography.Paragraph type="secondary">
        Consultez les permissions accordées aux médecins et révoquez-les à tout
        moment. Une révocation s’applique dès le prochain appel.
      </Typography.Paragraph>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <List
        locale={{ emptyText: "Aucune demande d’accès." }}
        dataSource={sortedItems}
        renderItem={(grant) => {
          const requested = Array.isArray(grant?.requestedScopes)
            ? grant.requestedScopes
            : Array.isArray(grant?.scopes)
              ? grant.scopes
              : [];
          const active = String(grant?.status).toLowerCase() === "accepted";
          return (
            <List.Item>
              <Card style={{ width: "100%" }}>
                <div style={{ display: "flex", gap: 16, justifyContent: "space-between" }}>
                  <div>
                    <Typography.Text strong>
                      Médecin {grant?.doctorName || grant?.doctorId || "—"}
                    </Typography.Text>
                    <div style={{ marginTop: 8 }}>
                      <Tag color={active ? "green" : "default"}>{grant?.status}</Tag>
                      {grant?.validUntil ? (
                        <Typography.Text type="secondary">
                          Jusqu’au {new Date(grant.validUntil).toLocaleDateString()}
                        </Typography.Text>
                      ) : null}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      {requested.map((scope) => (
                        <Tag key={scope}>{scopeLabels.get(scope) || scope}</Tag>
                      ))}
                    </div>
                  </div>
                  {active ? (
                    <Button
                      danger
                      loading={revokingId === grant.id}
                      onClick={() => revoke(grant)}
                    >
                      Révoquer
                    </Button>
                  ) : null}
                </div>
              </Card>
            </List.Item>
          );
        }}
      />
    </main>
  );
}
